import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Button, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Chip, Grid
} from '@mui/material';
import { ArrowBack, CheckCircle as ReceiveIcon, LocalShipping as SupplierIcon } from '@mui/icons-material';
import axios from 'axios';
import { getApiUrl } from '../config/api';
import { useParams, useNavigate } from 'react-router-dom';

const PurchaseOrderDetailPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [po, setPo] = useState<any>(null);

    const getHeaders = () => {
        const userInfo = localStorage.getItem('userInfo');
        const token = userInfo ? JSON.parse(userInfo).token : null;
        return { headers: { Authorization: `Bearer ${token}` } };
    };

    const fetchPODetails = React.useCallback(async () => {
        try {
            const res = await axios.get(getApiUrl(`purchases/${id}`), getHeaders());
            setPo(res.data);
        } catch (error) {
            console.error('Error fetching PO:', error);
        }
    }, [id]);

    useEffect(() => {
        if (id) fetchPODetails();
    }, [id, fetchPODetails]);

    const handleReceive = async () => {
        if (window.confirm('Are you sure you want to mark this order as received? This will update inventory stock and create accounting entries.')) {
            try {
                await axios.post(getApiUrl(`purchases/${id}/receive`), {}, getHeaders());
                fetchPODetails();
                alert('Order Received successfully');
            } catch (error) {
                console.error('Error receiving PO:', error);
                alert('Error receiving order');
            }
        }
    };

    if (!po) return <Typography sx={{ p: 4 }}>Loading...</Typography>;

    return (
        <Box sx={{ p: 3 }}>
            <Button
                startIcon={<ArrowBack />}
                onClick={() => navigate('/dashboard/purchases')}
                sx={{ mb: 2 }}
            >
                Back to Orders
            </Button>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1a237e' }}>
                    Purchase Order #{po.orderNo}
                </Typography>
                <Chip
                    label={po.status}
                    color={po.status === 'RECEIVED' ? 'success' : 'warning'}
                    sx={{ fontWeight: 'bold', fontSize: '1rem', px: 2 }}
                />
            </Box>

            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper sx={{ p: 3, borderRadius: 2, height: '100%' }}>
                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>Supplier Details</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                            <SupplierIcon color="action" />
                            <Typography variant="h6">{po.supplier?.name}</Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">{po.supplier?.address || 'No address'}</Typography>
                        <Typography variant="body2" color="text.secondary">{po.supplier?.email || 'No email'}</Typography>
                        <Typography variant="body2" color="text.secondary">{po.supplier?.phone || 'No phone'}</Typography>
                    </Paper>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper sx={{ p: 3, borderRadius: 2, height: '100%' }}>
                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>Order Info</Typography>
                        <Grid container spacing={1}>
                            <Grid size={{ xs: 6 }}><Typography variant="subtitle2">Order Date:</Typography></Grid>
                            <Grid size={{ xs: 6 }}><Typography variant="body2">{new Date(po.orderDate).toLocaleDateString()}</Typography></Grid>

                            <Grid size={{ xs: 6 }}><Typography variant="subtitle2">Expected Date:</Typography></Grid>
                            <Grid size={{ xs: 6 }}><Typography variant="body2">{po.expectedDate ? new Date(po.expectedDate).toLocaleDateString() : '-'}</Typography></Grid>

                            {po.receivedDate && (
                                <>
                                    <Grid size={{ xs: 6 }}><Typography variant="subtitle2">Received Date:</Typography></Grid>
                                    <Grid size={{ xs: 6 }}><Typography variant="body2">{new Date(po.receivedDate).toLocaleDateString()}</Typography></Grid>
                                </>
                            )}
                        </Grid>
                        {po.status === 'PENDING' && (
                            <Button
                                variant="contained"
                                color="success"
                                startIcon={<ReceiveIcon />}
                                fullWidth
                                sx={{ mt: 3 }}
                                onClick={handleReceive}
                            >
                                Mark as Received
                            </Button>
                        )}
                    </Paper>
                </Grid>
            </Grid>

            <Paper sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
                <Typography variant="h6" sx={{ p: 2, bgcolor: '#f5f5f5', fontWeight: 'bold' }}>
                    Items
                </Typography>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Product</TableCell>
                                <TableCell align="right">Quantity</TableCell>
                                <TableCell align="right">Unit Cost</TableCell>
                                <TableCell align="right">Subtotal</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {po.items?.map((item: any) => (
                                <TableRow key={item.id}>
                                    <TableCell>
                                        <Typography variant="subtitle2">{item.product?.name || 'Unknown Product'}</Typography>
                                    </TableCell>
                                    <TableCell align="right">{item.quantity}</TableCell>
                                    <TableCell align="right">${Number(item.unitCost).toFixed(2)}</TableCell>
                                    <TableCell align="right">${Number(item.subtotal).toFixed(2)}</TableCell>
                                </TableRow>
                            ))}
                            <TableRow>
                                <TableCell colSpan={3} align="right" sx={{ fontWeight: 'bold' }}>Total:</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                                    ${Number(po.totalAmount).toFixed(2)}
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {po.payments && po.payments.length > 0 && (
                <Paper sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
                    <Typography variant="h6" sx={{ p: 2, bgcolor: '#f5f5f5', fontWeight: 'bold' }}>
                        Payments Record
                    </Typography>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Payment No</TableCell>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Method</TableCell>
                                    <TableCell align="right">Amount</TableCell>
                                    <TableCell>Reference</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {po.payments.map((p: any) => (
                                    <TableRow key={p.id}>
                                        <TableCell sx={{ fontWeight: 'bold' }}>{p.paymentNo}</TableCell>
                                        <TableCell>{new Date(p.paymentDate).toLocaleDateString()}</TableCell>
                                        <TableCell>{p.paymentMethod}</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>${Number(p.amount).toFixed(2)}</TableCell>
                                        <TableCell>{p.reference || '-'}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}

            {po.entries && po.entries.length > 0 && (
                <Paper sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
                    <Typography variant="h6" sx={{ p: 2, bgcolor: '#f5f5f5', fontWeight: 'bold' }}>
                        Accounting Entries (General Ledger)
                    </Typography>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Description</TableCell>
                                    <TableCell align="right">Debit</TableCell>
                                    <TableCell align="right">Credit</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {po.entries.map((entry: any) => (
                                    <TableRow key={entry.id}>
                                        <TableCell>{new Date(entry.postedAt).toLocaleDateString()}</TableCell>
                                        <TableCell>{entry.description}</TableCell>
                                        <TableCell align="right">{Number(entry.debit) > 0 ? `$${Number(entry.debit).toFixed(2)}` : '-'}</TableCell>
                                        <TableCell align="right">{Number(entry.credit) > 0 ? `$${Number(entry.credit).toFixed(2)}` : '-'}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}
        </Box>
    );
};

export default PurchaseOrderDetailPage;
